/**
 * React Query Client Configuration
 * Configures caching, deduplication, and error handling for API requests
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Every React Query cache key, in one place. A hook that reads data and a mutation that refreshes
 * it must name the SAME key; a key typed twice silently stops refreshing the day one copy changes.
 * Use these — never a hand-typed array. Prefix keys (`…Any…`, `platform.all`) refresh every key
 * that starts with them.
 */
export const queryKeys = {
  eval: {
    probes: ['eval', 'probes'] as const,
    runs: ['eval', 'runs'] as const,
    runList: (limit: number) => ['eval', 'runs', limit] as const,
    /** Prefix of every run detail; also the "start run" mutation key. */
    anyRun: ['eval', 'run'] as const,
    run: (id: string) => ['eval', 'run', id] as const,
    dataset: (name: string) => ['eval', 'dataset', name] as const,
    scorecard: ['eval', 'scorecard'] as const,
  },
  platform: {
    all: ['platform'] as const,
    stacks: ['platform', 'stacks'] as const,
    clients: ['platform', 'clients'] as const,
    client: (id: string) => ['platform', 'client', id] as const,
    anyHistory: ['platform', 'history'] as const,
    history: (tenantId?: string) => ['platform', 'history', tenantId ?? 'all'] as const,
    anyAlerts: ['platform', 'alerts'] as const,
    alerts: (openOnly: boolean) => ['platform', 'alerts', openOnly] as const,
    signoff: ['platform', 'signoff'] as const,
  },
  system: {
    health: ['system', 'health'] as const,
    engineReachable: ['system', 'engine-reachable'] as const,
  },
  tenants: {
    all: ['tenants'] as const,
    userCounts: ['tenants', 'user-counts'] as const,
  },
  users: {
    all: ['users'] as const,
    accessOptions: ['accessOptions'] as const,
  },
  knowledgeBase: {
    /** Prefix of every user's knowledge-base list (refresh after an access change). */
    mineAll: ['userKnowledgeBases'] as const,
    /** The signed-in user's assigned, active knowledge bases — shared by Chat and the KB page.
     *  Keyed by user so one user's list can never be shown to another in the same browser. */
    mine: (userId: string | undefined) => ['userKnowledgeBases', userId] as const,
    departments: (userId: string | undefined) => ['knowledgeBase', 'departments', userId] as const,
    anyDocuments: ['knowledgeBase', 'documents'] as const,
    documents: (kbIds: string[]) => ['knowledgeBase', 'documents', kbIds] as const,
    dataSources: ['knowledgeBase', 'dataSources'] as const,
  },
  conversations: {
    forUser: (userId: string | undefined) => ['conversations', userId] as const,
  },
  ai: {
    modelStatus: ['ai', 'model-status'] as const,
  },
};

/**
 * Create and configure the React Query client
 * 
 * Key configurations:
 * - retry: 1 (only retry once on failure, not 3 times)
 * - staleTime: 5 minutes (data considered fresh for 5 min - enables deduplication)
 * - cacheTime: 10 minutes (cached data kept in memory for 10 min)
 * - refetchOnWindowFocus: false (don't refetch when user returns to tab)
 * - refetchOnReconnect: true (refetch when internet reconnects)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Only retry once on failure (not 3 times by default)
      // This prevents excessive retries and shows errors faster to users
      retry: 1,
      
      // Data is considered fresh for 5 minutes
      // Multiple components requesting same data within 5 min will use cached data
      // This enables request deduplication
      staleTime: 5 * 60 * 1000, // 5 minutes
      
      // Cached data is kept in memory for 10 minutes after last use
      // After this, data is garbage collected
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      
      // Don't refetch when user switches back to the browser tab
      // This prevents unnecessary API calls when switching windows
      refetchOnWindowFocus: false,
      
      // Refetch when internet connection is restored
      refetchOnReconnect: true,
      
      // Don't refetch on mount if data is still fresh
      refetchOnMount: false,
    },
    mutations: {
      // NEVER auto-retry a write. A retry re-sends a non-idempotent request: on 2026-10-10 a
      // 10-minute client timeout on "start eval run" made React Query re-POST it, so every run
      // started a SECOND run on the pod. The same retry could stage a model twice (two Vault
      // keys), activate twice or create a tenant twice. Failed writes surface to the user instead.
      retry: false,
    },
  },
});

